import React from "react";
import {Stack, Typography, Box, List, ListItem, IconButton, Icon, Button, Alert} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {useAttachments} from "../../hooks/attachmentsHooks";
import {getResourceErrorMessage, isResourceFailed} from "../../utils/resourceUtils";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {swallowException} from "../../utils/appUtils";
import {AttachmentsQuery} from "../../services/fieldService";
import {downloadBlob} from "../../utils/downloadUtils";

interface Props {
    companyId: string;
    query: AttachmentsQuery;
}

export const FieldAttachments: React.FC<Props> = ({companyId, query}) => {
    const {items: attachments, isLoading} = useAttachments(companyId, query);
    const fieldManagement = useFieldManagement();

    const downloadAll = () => {
        fieldManagement
            .getAttachmentsArchive(companyId, query)
            .then((data) => {
                downloadBlob(data, "archive.zip");
            })
            .catch(swallowException);
    };

    const downloadFile = (id: string, filename: string) => {
        fieldManagement
            .getAttachmentById(companyId, id)
            .then((data) => {
                downloadBlob(data, filename);
            })
            .catch(swallowException);
    };

    if (!attachments || attachments.length === 0) {
        return null;
    } else if (isLoading) {
        return null;
    } else {
        return (
            <>
                <Stack direction="column">
                    {isResourceFailed(fieldManagement.resource) && (
                        <Alert severity="error">{getResourceErrorMessage(fieldManagement.resource)}</Alert>
                    )}
                    <Stack direction="row" alignItems="center">
                        <Typography fontSize={20} fontWeight={500} textTransform="uppercase">
                            Attachments {attachments.length}
                        </Typography>
                        <Button
                            variant="text"
                            onClick={() => {
                                downloadAll();
                            }}
                        >
                            Download All
                        </Button>
                    </Stack>

                    <Box sx={{maxHeight: "400px", overflow: "auto"}}>
                        <List>
                            {attachments.map((attachment) => (
                                <ListItem key={attachment.id} divider>
                                    <Stack width="100%" direction="row" justifyContent="space-between">
                                        <div>
                                            <Icon color="primary">
                                                <DescriptionIcon />
                                            </Icon>
                                            {attachment.filename}
                                        </div>
                                        <IconButton
                                            aria-label="download"
                                            color="primary"
                                            onClick={() => {
                                                downloadFile(attachment.id, attachment.filename);
                                            }}
                                        >
                                            <FileDownloadIcon />
                                        </IconButton>
                                    </Stack>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                </Stack>
            </>
        );
    }
};
